import React from "react";
import { Upload, message } from "antd";
import InboxOutlined from "../../assets/images/upload-icon.svg";
import { Storage } from "aws-amplify";
import { mimeTypeLabels } from "../../constants/MiscKeys";

const { Dragger } = Upload;
const progressColor = "#87d068";

export interface UploadedFileItem {
  uid: string;
  name: string;
  status?: "uploading" | "done" | "error" | "removed";
  percent?: number;
  url?: string;
  s3Key?: string;
}

export interface FileUploaderProps {
  fileList: UploadedFileItem[];
  setFileList: (file) => void;

  maxCount?: number;
  maxSizeMB?: number;
  allowedFileTypes?: string[];
  storageLevel?: string;
  title?: string;
}

export const FileUploader = ({
  fileList,
  setFileList,
  maxCount = 1,
  maxSizeMB = 10,
  allowedFileTypes,
  storageLevel = "public",
  title = "Click or drag file to this area to upload",
}: FileUploaderProps) => {
  const getTypeHintText = () => {
    if (allowedFileTypes?.length) {
      const readableTypes = allowedFileTypes
        .map((t) => mimeTypeLabels[t] || t)
        .join(", ");
      return `Only ${readableTypes} files are allowed`;
    }
    return "";
  };

  const props = {
    name: "file",
    multiple: maxCount > 1,
    fileList: fileList,
    maxCount,

    beforeUpload: (file, batchFileList) => {
      const positionInBatch = batchFileList.indexOf(file);
      const totalIfAccepted = fileList.length + positionInBatch + 1;

      if (totalIfAccepted > maxCount) {
        message.error({
          content: `You can only upload up to ${maxCount} file${maxCount > 1 ? "s" : ""}.`,
          key: "max-count-warning",
        });
        return Upload.LIST_IGNORE;
      }
      if (allowedFileTypes?.length && !allowedFileTypes.includes(file.type)) {
        const readableTypes = allowedFileTypes
          .map((t) => mimeTypeLabels[t] || t)
          .join(", ");
        message.error(
          `Invalid file type. Only ${readableTypes} files are allowed.`,
        );
        return Upload.LIST_IGNORE;
      }
      const isUnderLimit = file.size / 1024 / 1024 < maxSizeMB;
      if (!isUnderLimit) {
        message.error(`File must be smaller than ${maxSizeMB}MB.`);
        return Upload.LIST_IGNORE;
      }
      return true;
    },

    customRequest: async ({ file, onSuccess, onError, onProgress }) => {
      const uniqueFileName = `${Date.now()}-${file.name}`;

      try {
        const result = await Storage.put(uniqueFileName, file, {
          level: storageLevel,
          contentType: file.type,
          progressCallback: (progress: { loaded: number; total: number }) => {
            const percent = Math.round(
              (progress.loaded / progress.total) * 100,
            );
            onProgress({ percent }, file);
          },
        });

        const fileURL = await Storage.get(result.key, { level: storageLevel });
        const cleanS3Link = fileURL.split("?")[0];

        setFileList((prev) => {
          const alreadyThere = prev.some((f) => f.uid === file.uid);
          const doneEntry = {
            uid: file.uid,
            name: file.name,
            status: "done",
            url: cleanS3Link,
            s3Key: result.key,
          };
          return alreadyThere
            ? prev.map((f) => (f.uid === file.uid ? { ...f, ...doneEntry } : f))
            : [...prev, doneEntry];
        });

        message.success({
          content: `${file.name} uploaded successfully.`,
          key: `upload-${file.uid}`,
        });

        onSuccess?.(result, file);
      } catch (error) {
        message.error({
          content: `${file.name} upload failed.`,
          key: `upload-${file.uid}`,
        });
        onError?.(error as Error);
      }
    },

    onChange: (info) => {
      setFileList((prev) =>
        info.fileList.map((f) => {
          const existing = prev.find((p) => p.uid === f.uid);
          return {
            uid: f.uid,
            name: f.name,
            status: f.status,
            percent: f.percent,
            url: existing?.url,
            s3Key: existing?.s3Key,
          };
        }),
      );
    },

    onRemove: async (file) => {
      const target = fileList.find((f) => f.uid === file.uid);

      if (target?.s3Key) {
        try {
          await Storage.remove(target.s3Key, { level: storageLevel });
        } catch (error) {
          message.error(`Failed to delete ${target.name} from storage.`);
          return false;
        }
      }

      setFileList((prev) => prev.filter((f) => f.uid !== file.uid));
      return true;
    },

    progress: {
      strokeColor: progressColor,
    },
  };

  return (
    <Dragger {...props}>
      <p className="ant-upload-drag-icon">
        <img src={InboxOutlined} />
      </p>
      <p className="ant-upload-text">{title}</p>
      {getTypeHintText() && (
        <p className="ant-upload-hint">{getTypeHintText()}</p>
      )}
    </Dragger>
  );
};
